import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useFolderStore } from '../../stores/folderStore';

export interface BulkImportItemNumberSettings {
  itemNumber: string;
  itemNumber2: string;
  itemNumberPrefix: boolean;
  itemNumberSuffix: boolean;
  itemNumberSerial: boolean;
  memoPrefix: boolean;
  memoSuffix: boolean;
  memoSerial: boolean;
}

export interface BulkImportCommonSettingsHandle {
  validate: () => string | null;
  resolveFolderId: () => Promise<number | undefined>;
  getItemNumberSettings: () => BulkImportItemNumberSettings | null;
}

interface BulkImportCommonSettingsProps {
  imageCount: number;
}

function ItemNumberFieldGroup({
  placeholder,
  value,
  onChange,
  prefix,
  suffix,
  serial,
  onPrefixChange,
  onSuffixChange,
  onSerialChange
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  prefix: boolean;
  suffix: boolean;
  serial: boolean;
  onPrefixChange: (v: boolean) => void;
  onSuffixChange: (v: boolean) => void;
  onSerialChange: (v: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-[30px] px-2 rounded-md border border-gray-300 bg-gray-50 text-sm"
      />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={prefix}
            onChange={(e) => {
              onPrefixChange(e.target.checked);
              if (e.target.checked) onSuffixChange(false);
            }}
            className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
          />
          <span className="text-xs text-gray-600">文頭</span>
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={suffix}
            onChange={(e) => {
              onSuffixChange(e.target.checked);
              if (e.target.checked) onPrefixChange(false);
            }}
            className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
          />
          <span className="text-xs text-gray-600">文末</span>
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={serial}
            onChange={(e) => onSerialChange(e.target.checked)}
            className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
          />
          <span className="text-xs text-gray-600">連番</span>
        </label>
      </div>
    </div>
  );
}

export const BulkImportCommonSettings = forwardRef<
  BulkImportCommonSettingsHandle,
  BulkImportCommonSettingsProps
>(function BulkImportCommonSettings({ imageCount }, ref) {
  const [createNewFolder, setCreateNewFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [applyItemNumber, setApplyItemNumber] = useState(false);
  const [itemNumber, setItemNumber] = useState('');
  const [itemNumber2, setItemNumber2] = useState('');
  const [itemNumberPrefix, setItemNumberPrefix] = useState(false);
  const [itemNumberSuffix, setItemNumberSuffix] = useState(false);
  const [itemNumberSerial, setItemNumberSerial] = useState(false);
  const [memoPrefix, setMemoPrefix] = useState(false);
  const [memoSuffix, setMemoSuffix] = useState(false);
  const [memoSerial, setMemoSerial] = useState(false);
  const [folderError, setFolderError] = useState<string | null>(null);

  const { createFolder, resolveDefaultFolderId } = useFolderStore();

  const generateSuggestedName = () => {
    const now = new Date();
    const dateStr = now
      .toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      .replace(/\//g, '-');
    return `一括インポート_${dateStr}_${imageCount}枚`;
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (!createNewFolder) return null;
      const trimmed = folderName.trim();
      if (!trimmed) return 'フォルダ名を入力してください';
      if (trimmed.length > 50) return 'フォルダ名は50文字以内で入力してください';
      return null;
    },

    resolveFolderId: async () => {
      if (createNewFolder) {
        const trimmed = folderName.trim();
        if (!trimmed) return undefined;
        return createFolder(trimmed);
      }
      return resolveDefaultFolderId();
    },

    getItemNumberSettings: () => {
      if (!applyItemNumber) return null;
      return {
        itemNumber,
        itemNumber2,
        itemNumberPrefix,
        itemNumberSuffix,
        itemNumberSerial,
        memoPrefix,
        memoSuffix,
        memoSerial
      };
    }
  }));

  return (
    <div className="border border-purple-200 rounded-lg p-3 sm:p-4 bg-gradient-to-r from-indigo-50/50 via-purple-50/50 to-pink-50/50">
      <h3 className="text-sm font-medium text-gray-800 mb-3 sm:mb-4">全商品共通設定</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
        {/* Folder — full width on mobile, left half on md+ */}
        <div className="flex flex-col gap-2 md:pr-4 min-w-0">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={createNewFolder}
              onChange={(e) => {
                setCreateNewFolder(e.target.checked);
                setFolderError(null);
                if (!e.target.checked) setFolderName('');
              }}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
            />
            <span className="text-sm font-medium text-gray-700">新しいフォルダを作成</span>
          </label>
          {createNewFolder && (
            <div className="ml-5 sm:ml-6 space-y-1 min-w-0">
              <input
                type="text"
                value={folderName}
                onChange={(e) => {
                  setFolderName(e.target.value);
                  setFolderError(null);
                }}
                placeholder="フォルダ名を入力"
                maxLength={50}
                className={`block w-full px-3 py-2 text-sm rounded-md border shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                  folderError ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => {
                  setFolderName(generateSuggestedName());
                  setFolderError(null);
                }}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                候補名を使用
              </button>
              {folderError && (
                <p className="text-xs text-red-600">{folderError}</p>
              )}
            </div>
          )}
        </div>

        {/* Item numbers — below folder on mobile, right half on md+ */}
        <div className="flex flex-col gap-2 min-w-0 pt-4 md:pt-0 border-t md:border-t-0 border-purple-100 md:pl-4 md:border-l">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={applyItemNumber}
              onChange={(e) => setApplyItemNumber(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
            />
            <span className="text-sm font-medium text-gray-700">管理番号を一括適用</span>
          </label>

          {applyItemNumber && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-1">
              <ItemNumberFieldGroup
                placeholder="管理番号1"
                value={itemNumber}
                onChange={setItemNumber}
                prefix={itemNumberPrefix}
                suffix={itemNumberSuffix}
                serial={itemNumberSerial}
                onPrefixChange={setItemNumberPrefix}
                onSuffixChange={setItemNumberSuffix}
                onSerialChange={setItemNumberSerial}
              />
              <ItemNumberFieldGroup
                placeholder="管理番号2"
                value={itemNumber2}
                onChange={setItemNumber2}
                prefix={memoPrefix}
                suffix={memoSuffix}
                serial={memoSerial}
                onPrefixChange={setMemoPrefix}
                onSuffixChange={setMemoSuffix}
                onSerialChange={setMemoSerial}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
